import React, { useCallback, useEffect, useState } from 'react';
import { Download, PiggyBank, Search } from 'lucide-react';
import api from '../../../utils/api';
import toast from 'react-hot-toast';
import { isoDate } from '../../../utils/time';
import {
  PAGE_WIDE, PageHeader, KpiTile, SectionCard, EmptyState, ErrorState, TableSkeleton, ProgressBar, money, num, plural,
} from '../../../components/ui';
import {
  TABLE_WRAP, TABLE, THEAD_ROW, TH, TH_NUM, TR, TD, TD_NUM, TFOOT_ROW, MoneyCell,
  DateRange, RefreshButton,
} from '../financeKit';

function downloadCSV(filename, headers, rows) {
  const lines = [headers.join(','), ...rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// A labelled search box that lines up with the date fields beside it.
function SearchField({ id, label, value, onChange, placeholder }) {
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-[12px] font-medium text-slate-500">{label}</label>
      <div className="relative">
        <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
        <input id={id} type="search" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
          className="input-field !w-48 !py-2 !pl-8 text-sm" />
      </div>
    </div>
  );
}

export default function ClientFunding() {
  const [data, setData]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const today = new Date();
  const [from, setFrom] = useState(() => {
    const d = new Date(today.getFullYear(), today.getMonth(), 1);
    return isoDate(d);
  });
  const [to, setTo] = useState(() => isoDate(today));

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/financials/reports/client-funding?from=${from}&to=${to}`);
      setData(r.data || []);
    } catch (e) {
      setData([]);
      setError(e.response?.data?.message || "We couldn't load client funding. Check your connection, then try again.");
    } finally { setLoading(false); }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const filtered = search
    ? data.filter(d => d.client_name?.toLowerCase().includes(search.toLowerCase()))
    : data;

  const totalCharged = filtered.reduce((s, d) => s + parseFloat(d.total_charged || 0), 0);
  const totalShifts  = filtered.reduce((s, d) => s + parseInt(d.shifts || 0), 0);
  const totalHours   = filtered.reduce((s, d) => s + parseFloat(d.total_hours || 0), 0);

  // Each row worked out once; the totals row adds up exactly what the rows show.
  const rows = filtered.map((row) => {
    const budget    = parseFloat(row.ndis_budget || 0);
    const billed    = parseFloat(row.total_charged || 0);
    const remaining = budget - billed;
    const pct       = budget > 0 ? ((billed / budget) * 100) : 0;
    const isOver    = budget > 0 && billed > budget;
    return { row, budget, billed, remaining, pct, isOver };
  });
  const budgeted       = rows.filter(r => r.budget > 0);
  const sumBudget      = budgeted.reduce((s, r) => s + r.budget, 0);
  const sumRemaining   = budgeted.reduce((s, r) => s + r.remaining, 0);
  const overCount      = rows.filter(r => r.isOver).length;

  function exportCSV() {
    const headers = ['Client','Shifts','Hours','Billed','NDIS Budget','Utilisation %','Remaining'];
    const rows = filtered.map(row => {
      const budget = parseFloat(row.ndis_budget || 0);
      const billed = parseFloat(row.total_charged || 0);
      const pct = budget > 0 ? ((billed / budget) * 100).toFixed(1) : 'N/A';
      const remaining = budget > 0 ? (budget - billed).toFixed(2) : 'N/A';
      return [row.client_name, row.shifts, Number(row.total_hours || 0).toFixed(1),
        billed.toFixed(2), budget > 0 ? budget.toFixed(2) : '', pct, remaining];
    });
    downloadCSV(`client-funding-${from}-to-${to}.csv`, headers, rows);
    toast.success(`Exported ${plural(filtered.length, 'client')}`);
  }

  return (
    <div className={PAGE_WIDE}>
      <PageHeader
        title="Client funding"
        description="What each client was billed from approved shifts in the range, against their current NDIS budget."
        actions={(
          <>
            <SearchField id="funding-search" label="Search" value={search} onChange={setSearch} placeholder="Client name" />
            <DateRange from={from} to={to} onFrom={setFrom} onTo={setTo} idPrefix="funding" />
            <RefreshButton onClick={load} loading={loading} label="Refresh report" />
            <button type="button" onClick={exportCSV} disabled={filtered.length === 0} className="btn-secondary">
              <Download size={14} /> Export CSV
            </button>
          </>
        )}
      />

      {error ? (
        <div className="card"><ErrorState message={error} onRetry={load} /></div>
      ) : (
        <>
          {/* Summary — equals the totals row below. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiTile label="Billed to clients" value={loading ? '…' : money(totalCharged)} accent="primary"
              sub={loading ? null : 'Approved shifts in range'} />
            <KpiTile label="Clients" value={loading ? '…' : num(filtered.length)} accent="slate"
              sub={loading || !overCount ? null : `${num(overCount)} over budget`} />
            <KpiTile label="Shifts" value={loading ? '…' : num(totalShifts)} accent="slate" />
            <KpiTile label="Hours" value={loading ? '…' : num(totalHours, 1)} accent="slate" />
          </div>

          <SectionCard title={`Funding by client${loading ? '' : ` · ${filtered.length}`}`} icon={PiggyBank}>
            {loading ? (
              <TableSkeleton rows={6} cols={7} />
            ) : filtered.length === 0 ? (
              search && data.length > 0 ? (
                <EmptyState icon={Search} title="No clients match your search"
                  hint="Check the spelling, or clear the search."
                  action={<button type="button" onClick={() => setSearch('')} className="btn-secondary">Clear search</button>} />
              ) : (
                <EmptyState icon={PiggyBank} title="No billed shifts in this range"
                  hint="Clients appear here once their shifts have an approved timesheet. If approved shifts are missing, run Recalculate on the Financials dashboard." />
              )
            ) : (
              <div className={TABLE_WRAP}>
                <table className={TABLE}>
                  <thead>
                    <tr className={THEAD_ROW}>
                      <th scope="col" className={TH}>Client</th>
                      <th scope="col" className={TH_NUM}>Shifts</th>
                      <th scope="col" className={TH_NUM}>Hours</th>
                      <th scope="col" className={TH_NUM}>Billed</th>
                      <th scope="col" className={TH_NUM}>NDIS budget</th>
                      <th scope="col" className={`${TH} w-44`}>Utilisation</th>
                      <th scope="col" className={TH_NUM}>Remaining</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(({ row, budget, billed, remaining, pct, isOver }, i) => (
                      <tr key={row.id || i} className={TR}>
                        <td className={`${TD} font-medium text-slate-800`}>{row.client_name}</td>
                        <td className={TD_NUM}>{num(row.shifts)}</td>
                        <td className={TD_NUM}>{num(row.total_hours, 1)}</td>
                        <td className={TD_NUM}><MoneyCell value={billed} /></td>
                        <td className={TD_NUM}>
                          {budget > 0 ? <MoneyCell value={budget} /> : <span className="text-slate-500">—</span>}
                        </td>
                        <td className={TD}>
                          {budget > 0 ? (
                            <div className="space-y-1">
                              <ProgressBar pct={pct} tone={isOver ? 'rose' : pct > 90 ? 'amber' : 'primary'} />
                              <p className={`text-[12px] font-medium tabular-nums ${isOver ? 'text-red-600' : pct > 90 ? 'text-amber-700' : 'text-slate-500'}`}>
                                {num(pct, 1)}%{isOver ? ' · over budget' : ''}
                              </p>
                            </div>
                          ) : <span className="text-[12px] text-slate-500">No budget set</span>}
                        </td>
                        <td className={TD_NUM}>
                          {budget > 0 ? <MoneyCell value={remaining} /> : <span className="text-slate-500">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className={TFOOT_ROW}>
                      <td className={TD}>Total</td>
                      <td className={TD_NUM}>{num(totalShifts)}</td>
                      <td className={TD_NUM}>{num(totalHours, 1)}</td>
                      <td className={TD_NUM}><MoneyCell value={totalCharged} strong /></td>
                      <td className={TD_NUM}>{budgeted.length ? <MoneyCell value={sumBudget} strong /> : null}</td>
                      <td className={TD} />
                      <td className={TD_NUM}>{budgeted.length ? <MoneyCell value={sumRemaining} strong /> : null}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
            {!loading && budgeted.length > 0 && budgeted.length < rows.length && (
              <p className="px-4 py-2.5 text-xs text-slate-500 border-t border-slate-100">
                Budget and remaining totals cover only the {plural(budgeted.length, 'client')} with a budget set.
              </p>
            )}
          </SectionCard>
        </>
      )}
    </div>
  );
}
